import type { CSSProperties, FC, ReactNode } from 'react'
import type { GetStaticProps } from 'next'
import type { Project } from '@/types'

import { useMemo, useRef, useState } from 'react'
import { ArrowRight, ArrowUpRight } from 'lucide-react'
import { cn } from '@/lib/utils'
import { getProjects } from '@/lib/content'
import { gsap, MOTION, useGSAP } from '@/lib/motion'
import { Layout } from '@/components/generals/layout'
import { QuoteLink } from '@/components/generals/quote-link'
import { Seo } from '@/components/generals/seo'
import { SplitWords } from '@/components/generals/split-words'
import { ToneCanvas } from '@/components/generals/tone-canvas'

const ALL = 'All'

interface WorkPageProps {
    projects: Array<Project>
}

interface ArchiveCardProps {
    project: Project
    index: number
}

const ArchiveCard: FC<ArchiveCardProps> = ({ project, index }): ReactNode => {
    const frame = (
        <span className='relative block aspect-[3/4] overflow-hidden bg-ink-soft'>
            {project.screenshot && (
                <img
                    src={project.screenshot}
                    alt={project.screenshotAlt ?? ''}
                    loading='lazy'
                    decoding='async'
                    className='absolute inset-0 size-full object-cover object-top transition-[object-position] duration-[7s] ease-linear group-hover:object-bottom motion-reduce:transition-none'
                />
            )}

            <span aria-hidden='true' className='pointer-events-none absolute inset-0 ring-1 ring-inset ring-paper/10' />
        </span>
    )

    return (
        <article data-card='' aria-labelledby={`archive-${project.id}`} className='group'>
            {project.demoUrl ? (
                <a
                    href={project.demoUrl}
                    target='_blank'
                    rel='noopener noreferrer'
                    tabIndex={-1}
                    aria-hidden='true'
                    data-cursor="Preview"
                    className='block'
                >
                    {frame}
                </a>
            ) : frame}

            <div className='mt-4 flex items-start justify-between gap-4'>
                <div className='min-w-0'>
                    <p className='label text-haze'>{String(index + 1).padStart(2, '0')}</p>

                    <h2 id={`archive-${project.id}`} className='mt-1.5 text-xl font-semibold leading-tight tracking-[-0.01em]'>
                        <span data-mdlfy-cms={`projects:${project.id}:title`}>{project.title}</span>
                    </h2>

                    <p className='label mt-1.5 text-haze'>
                        <span data-mdlfy-cms={`projects:${project.id}:industry`}>{project.industry}</span>
                    </p>
                </div>

                {project.demoUrl && (
                    <a
                        href={project.demoUrl}
                        target='_blank'
                        rel='noopener noreferrer'
                        className='label inline-flex shrink-0 items-center gap-1.5 pt-6 transition-colors hover:text-signal'
                    >
                        Live preview
                        <ArrowUpRight aria-hidden='true' className='size-3.5' />
                        <span className='sr-only'> of this design, opens in a new tab</span>
                    </a>
                )}
            </div>
        </article>
    )
}

const WorkPage: FC<WorkPageProps> = ({ projects }): ReactNode => {
    const ref = useRef<HTMLDivElement>(null)
    const [industry, setIndustry] = useState(ALL)

    const industries = useMemo(() => {
        const counts = new Map<string, number>()

        projects.forEach(project => counts.set(project.industry, (counts.get(project.industry) ?? 0) + 1))

        return Array.from(counts.entries()).sort((a, b) => b[1] - a[1] || a[0].localeCompare(b[0]))
    }, [projects])

    const visible = useMemo(
        () => (industry === ALL ? projects : projects.filter(project => project.industry === industry)),
        [projects, industry]
    )

    useGSAP(() => {
        const root = ref.current

        if (!root) return

        const mm = gsap.matchMedia()

        mm.add(MOTION.ok, () => {
            gsap.fromTo(root.querySelectorAll('[data-card]'), { autoAlpha: 0, y: 40 }, {
                autoAlpha: 1,
                y: 0,
                duration: 0.9,
                stagger: 0.05,
                ease: 'expo.out',
                clearProps: 'transform'
            })
        })

        return () => mm.revert()
    }, { scope: ref, dependencies: [industry], revertOnUpdate: true })

    return (
        <Layout>
            <Seo
                title="Design examples"
                description="Browse website design examples from Localboost, from plumbers and roofers to restaurants, salons and law firms. What we create for you will be uniquely you."
            />
            <ToneCanvas initial='ink' />

            <section
                data-tone='ink'
                data-surface='dark'
                aria-labelledby='work-title'
                className='relative bg-ink px-(--gutter) pb-[8vh] pt-[calc(var(--header-h)+14vh)] text-paper'
            >
                <p className='label fade text-haze'>
                    Sample designs, {projects.length} examples
                </p>

                <h1
                    id='work-title'
                    className='display mt-6 text-[clamp(3.6rem,13vw,15rem)] leading-[0.82] wdth-75'
                >
                    <SplitWords text="Design examples" stagger={0.08} innerClassName='rise' />
                </h1>

                <div className='mt-10 grid gap-8 md:grid-cols-12'>
                    <p className='fade max-w-[38ch] text-lg leading-relaxed text-haze md:col-span-5 md:text-xl' style={{ '--d': '0.3s' } as CSSProperties}>
                        Get some inspiration for your next website! What we create for you will be uniquely you.
                    </p>
                </div>
            </section>

            <section
                data-tone='ink'
                data-surface='dark'
                aria-label="Design examples by industry"
                className='relative bg-ink px-(--gutter) pb-[16vh] text-paper'
            >
                <div className='sticky top-0 z-20 -mx-[var(--gutter)] border-b border-paper/10 bg-ink/85 px-(--gutter) py-4 backdrop-blur-md'>
                    <div role='group' aria-label="Filter by industry" className='no-scrollbar flex gap-2 overflow-x-auto'>
                        {[[ALL, projects.length] as [string, number], ...industries].map(([name, count]) => {
                            const active = industry === name

                            return (
                                <button
                                    key={name}
                                    type='button'
                                    aria-pressed={active}
                                    onClick={() => setIndustry(name)}
                                    className={cn(
                                        'inline-flex h-10 shrink-0 items-center gap-2 border px-4 text-sm font-semibold transition-colors duration-300',
                                        active ? 'border-signal bg-signal text-ink' : 'border-paper/20 text-paper hover:border-paper/60'
                                    )}
                                >
                                    {name}
                                    <span className={cn('label text-[0.6rem]', active ? 'text-ink/70' : 'text-haze')}>{count}</span>
                                </button>
                            )
                        })}
                    </div>
                </div>

                <p aria-live='polite' className='label mt-8 text-haze'>
                    Showing {visible.length} of {projects.length}
                </p>

                {visible.length > 0 ? (
                    <div
                        ref={ref}
                        className='mt-8 grid gap-x-[3vw] gap-y-16 sm:grid-cols-2 lg:grid-cols-3 lg:[&>*:nth-child(3n+2)]:translate-y-[10vh]'
                    >
                        {visible.map((project, index) => (
                            <ArchiveCard key={project.id} project={project} index={index} />
                        ))}
                    </div>
                ) : (
                    <p className='mt-16 max-w-[40ch] text-lg text-haze'>
                        New design examples are on their way. Check back soon.
                    </p>
                )}
            </section>

            <section
                data-tone='signal'
                data-surface='light'
                aria-labelledby='work-cta-title'
                className='relative bg-signal px-(--gutter) py-[16vh] text-ink'
            >
                <div className='grid items-end gap-10 md:grid-cols-12'>
                    <h2
                        id='work-cta-title'
                        className='display text-[clamp(2.8rem,7vw,8.5rem)] leading-[0.88] wdth-87 md:col-span-8'
                    >
                        What we create for you will be uniquely you.
                    </h2>

                    <div className='md:col-span-4 md:justify-self-end'>
                        <QuoteLink
                            service='web-design'
                            className='group inline-flex h-16 items-center gap-3 bg-ink px-8 text-base font-semibold text-paper transition-colors duration-300 hover:bg-paper hover:text-ink'
                        >
                            Get a free quote
                            <ArrowRight aria-hidden='true' className='size-5 transition-transform duration-300 group-hover:translate-x-1' />
                        </QuoteLink>
                    </div>
                </div>
            </section>
        </Layout>
    )
}

export const getStaticProps: GetStaticProps<WorkPageProps> = async () => {
    const projects = await getProjects()

    return {
        props: { projects },
        revalidate: projects.length > 0 ? 60 : 10
    }
}

export default WorkPage
